export type SupportedLocale = 'en' | 'es' | 'fr' | 'de' | 'ca';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'es', 'fr', 'de', 'ca'];

export const LOCALE_STORAGE_KEY = 'scribane-locale';

const BCP47_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  ca: 'ca-ES',
};

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return value === 'en' || value === 'es' || value === 'fr' || value === 'de' || value === 'ca';
}

export function toBcp47Locale(locale: string): string {
  if (isSupportedLocale(locale)) {
    return BCP47_BY_LOCALE[locale];
  }
  return BCP47_BY_LOCALE.en;
}

export function localeFromAcceptLanguages(languages: readonly string[]): SupportedLocale {
  for (const language of languages) {
    const prefix = language.toLowerCase().split('-')[0];
    if (prefix === 'ca') return 'ca';
    if (prefix === 'fr') return 'fr';
    if (prefix === 'de') return 'de';
    if (prefix === 'es') return 'es';
    if (prefix === 'en') return 'en';
  }
  return 'en';
}

function readStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isSupportedLocale(stored)) {
      return stored;
    }
  } catch {
    // ignore
  }
  return null;
}

export function persistLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}

function browserLanguages(): string[] {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return [...navigator.languages];
  }
  return navigator.language ? [navigator.language] : [];
}

export function detectLocale(): SupportedLocale {
  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }
  return localeFromAcceptLanguages(browserLanguages());
}
